import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, X, Link2, ImageOff, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { api, mediaUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";

const statusMeta = {
  pending: { label: "Pendiente de tu aprobación", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Aprobado", cls: "bg-green-100 text-green-800 border-green-200" },
  rejected: { label: "Cambios solicitados", cls: "bg-red-100 text-red-800 border-red-200" },
};

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null); // { images, index }
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data: d } = await api.get(`/share/public/${token}`);
      setData(d);
      setShowForm(d.status === "pending");
      if (d.reviewer_name) setName(d.reviewer_name);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Este enlace no es válido o fue revocado.");
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    if (!name.trim()) return toast.error("Escribe tu nombre para registrar la respuesta");
    setSubmitting(true);
    try {
      await api.post(`/share/public/${token}/decision`, { decision, name: name.trim(), comment: comment.trim() });
      toast.success(decision === "approved" ? "¡Gracias! Tu aprobación quedó registrada." : "Gracias, tu solicitud de cambios fue enviada.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo registrar tu respuesta"); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F5F2EC] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-violet" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-[#F5F2EC] flex flex-col items-center justify-center p-6 text-center" data-testid="share-error">
      <Link2 className="w-10 h-10 text-navy/30 mb-4" />
      <h1 className="text-xl font-medium text-navy mb-1">Enlace no disponible</h1>
      <p className="text-sm text-navy/50 max-w-sm">{error}</p>
    </div>
  );

  const meta = statusMeta[data.status] || statusMeta.pending;
  const images = data.kind === "moodboard" ? (data.moodboard?.images || []) : (data.concept?.images || []);
  const openLb = (i) => setLightbox({ images, index: i });
  const lbPrev = () => setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));
  const lbNext = () => setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.images.length }));

  return (
    <div className="min-h-screen bg-[#F5F2EC] text-navy" data-testid="share-page">
      {/* Top bar */}
      <div className="border-b border-navy/10 bg-white/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold tracking-tight text-lg">OMNIA<span className="text-violet"> OS</span></span>
            <span className="text-navy/30">·</span>
            <span className="text-sm text-navy/50">{data.studio_label}</span>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full border ${meta.cls}`} data-testid="share-page-status">{meta.label}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-8 space-y-8">
        {/* Header */}
        <div>
          <div className="overline text-navy/40 mb-1">{data.kind === "moodboard" ? "Moodboard" : "Concept Board"}{data.project_name ? ` · ${data.project_name}` : ""}</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{data.title || (data.kind === "moodboard" ? "Moodboard" : "Concept Board")}</h1>
          {data.client && <p className="text-sm text-navy/50 mt-1">Preparado para {data.client}</p>}
        </div>

        {/* Decision result banner */}
        {data.status !== "pending" && (
          <div className={`rounded-xl border p-4 ${meta.cls}`} data-testid="share-decision-banner">
            <div className="flex items-center gap-2 font-medium">
              {data.status === "approved" ? <ShieldCheck className="w-5 h-5" /> : <X className="w-5 h-5" />}
              {data.status === "approved" ? "Aprobado" : "Cambios solicitados"}{data.reviewer_name ? ` por ${data.reviewer_name}` : ""}
            </div>
            {data.reviewer_comment && <p className="text-sm mt-1 opacity-90">"{data.reviewer_comment}"</p>}
          </div>
        )}

        {/* Concept details */}
        {data.kind === "concept" && data.concept && (
          <>
            {data.concept.concept && (
              <div className="bg-white border border-navy/10 rounded-xl p-6">
                <div className="overline text-navy/40 mb-1">Dirección creativa</div>
                <p className="text-navy/80 leading-relaxed">{data.concept.concept}</p>
              </div>
            )}
            {data.concept.keywords?.length > 0 && (
              <div>
                <div className="overline text-navy/40 mb-2">Mood / palabras clave</div>
                <div className="flex flex-wrap gap-2">{data.concept.keywords.map((k, i) => <span key={i} className="text-sm px-3 py-1 rounded-full bg-white border border-navy/10 text-navy/70">{k}</span>)}</div>
              </div>
            )}
            {data.concept.palette?.length > 0 && (
              <div>
                <div className="overline text-navy/40 mb-2">Paleta de color</div>
                <div className="flex flex-wrap gap-3">{data.concept.palette.map((c, i) => (
                  <div key={i} className="text-center">
                    <div className="w-16 h-16 rounded-lg border border-navy/10 shadow-sm" style={{ background: c.hex }} />
                    <div className="text-[11px] mt-1 text-navy/60">{c.name}</div>
                    <div className="text-[10px] text-navy/35">{c.hex}</div>
                  </div>
                ))}</div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {data.concept.typography && (<div className="bg-white border border-navy/10 rounded-xl p-5"><div className="overline text-navy/40 mb-1">Tipografía</div><p className="text-sm text-navy/70">{data.concept.typography}</p></div>)}
              {data.concept.materials?.length > 0 && (<div className="bg-white border border-navy/10 rounded-xl p-5"><div className="overline text-navy/40 mb-2">Materiales y acabados</div><ul className="text-sm text-navy/70 space-y-1">{data.concept.materials.map((m, i) => <li key={i}>· {m}</li>)}</ul></div>)}
            </div>
          </>
        )}

        {/* Moodboard notes */}
        {data.kind === "moodboard" && data.moodboard?.notes && (
          <div className="bg-white border border-navy/10 rounded-xl p-6">
            <div className="overline text-navy/40 mb-1">Notas</div>
            <p className="text-navy/80 leading-relaxed whitespace-pre-line">{data.moodboard.notes}</p>
          </div>
        )}

        {/* Images */}
        {images.length > 0 ? (
          <div>
            <div className="overline text-navy/40 mb-2">Imágenes</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="share-images">
              {images.map((u, i) => (
                <button key={i} onClick={() => openLb(i)} data-testid={`share-image-${i}`} className="aspect-square rounded-xl overflow-hidden border border-navy/10 bg-white group">
                  <img src={mediaUrl(u)} alt={`imagen ${i + 1}`} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-navy/40 py-10 border border-dashed border-navy/15 rounded-xl"><ImageOff className="w-6 h-6 mx-auto mb-2" />Aún no hay imágenes en este {data.kind === "moodboard" ? "moodboard" : "concept board"}.</div>
        )}

        {/* Approval form */}
        <div className="bg-white border border-navy/10 rounded-xl p-6 space-y-3" data-testid="share-approval">
          <div className="font-medium">{data.status === "pending" ? "Tu opinión" : "Actualizar tu respuesta"}</div>
          {!showForm ? (
            <button onClick={() => setShowForm(true)} data-testid="share-reopen-form" className="text-sm text-violet hover:underline">Cambiar mi respuesta</button>
          ) : (
            <>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" data-testid="share-name"
                className="w-full bg-[#F5F2EC] border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm" />
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Comentarios (opcional)" data-testid="share-comment"
                className="w-full bg-[#F5F2EC] border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm" />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => decide("approved")} disabled={submitting} data-testid="share-approve"
                  className="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-5 py-2.5 text-sm hover:bg-green-700 disabled:opacity-50 transition-colors">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Aprobar
                </button>
                <button onClick={() => decide("rejected")} disabled={submitting} data-testid="share-reject"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-sm hover:bg-navy/5 disabled:opacity-50 transition-colors">
                  <X className="w-4 h-4" /> Solicitar cambios
                </button>
              </div>
            </>
          )}
        </div>

        <div className="text-center text-xs text-navy/30 pt-4">Enviado con OMNIA OS</div>
      </div>

      {/* Lightbox */}
      {lightbox && lightbox.images.length > 0 && (
        <div className="fixed inset-0 z-[80] bg-navy/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="share-lightbox">
          <div className="absolute top-4 left-4 text-white/60 text-sm">{lightbox.index + 1} / {lightbox.images.length}</div>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="w-7 h-7" /></button>
          {lightbox.images.length > 1 && <button onClick={(e) => { e.stopPropagation(); lbPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 rounded-full p-2"><ChevronLeft className="w-6 h-6" /></button>}
          <img src={mediaUrl(lightbox.images[lightbox.index])} alt="" onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl" />
          {lightbox.images.length > 1 && <button onClick={(e) => { e.stopPropagation(); lbNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 rounded-full p-2"><ChevronRight className="w-6 h-6" /></button>}
        </div>
      )}
    </div>
  );
}
